<div class="main pt-3">
  <div class="container" style="max-width: 1200px">
    @if (loading) {
      <div class="card">
        <div class="card-body d-flex justify-content-center align-items-center py-20">
          <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden">{{ 'common.loading' | translate }}</span>
          </div>
        </div>
      </div>
    } @else if (!selectedCategory) {
      <!-- Category Selection -->
      <div class="card mb-5">
        <div class="card-body">
          <div class="d-flex justify-content-between align-items-center mb-4">
            <div>
              <h2 class="mb-1">{{ 'eventWizard.createNewEvent' | translate }}</h2>
              <p class="text-muted mb-0">{{ 'eventWizard.selectEventType' | translate }}</p>
            </div>
            <app-button
              [variant]="'ghost'"
              [label]="'common.cancel' | translate"
              [iconClass]="'fa fa-times'"
              (clicked)="cancel()"
            />
          </div>

          <div class="category-cards">
            @for (category of categoryOptions; track category.id) {
              <div class="category-card"
                   [class.disabled]="categoryRequiresSubscription(category)"
                   (click)="!categoryRequiresSubscription(category) && selectCategory(category.id)">
                <div class="category-icon" [style.background-color]="category.color">
                  <i class="fa {{ category.icon }}"></i>
                </div>
                <h4 class="category-title">{{ category.title }}</h4>
                <p class="category-description">{{ category.description }}</p>
                @if (categoryRequiresSubscription(category)) {
                  <div class="subscription-required">
                    <i class="fa fa-crown text-primary me-2"></i>
                    <span>{{ 'companyProfile.subscription.proRequired' | translate }}</span>
                  </div>
                  <a routerLink="/company-profile" [queryParams]="{tab: 'information'}" class="btn btn-primary btn-sm mt-2">
                    <i class="fa fa-arrow-right me-1"></i>
                    {{ 'companyProfile.subscription.upgradeNow' | translate }}
                  </a>
                } @else {
                  <div class="category-select">
                    <span>{{ 'eventWizard.select' | translate }}</span>
                    <i class="fa fa-arrow-right ms-2"></i>
                  </div>
                }
              </div>
            }
          </div>
        </div>
      </div>
    } @else {
      <!-- Wizard Header -->
      <div class="card mb-5">
        <div class="card-body">
          <div class="d-flex justify-content-between align-items-center mb-4">
            <div class="d-flex align-items-center gap-3">
              <button class="btn btn-light btn-sm" (click)="goBackToCategories()" [disabled]="eventId">
                <i class="fa fa-arrow-left me-1"></i>
                {{ 'common.back' | translate }}
              </button>
              <div>
                <h2 class="mb-1">
                  {{ 'eventWizard.create' | translate }} {{ selectedCategory === 'competition' ? ('eventWizard.competition' | translate) : selectedCategory === 'training' ? ('eventWizard.training' | translate) : ('eventWizard.socialEvent' | translate) }}
                </h2>
                <p class="text-muted mb-0">{{ 'eventWizard.followSteps' | translate }}</p>
              </div>
            </div>
            <app-button
              [variant]="'ghost'"
              [label]="'common.cancel' | translate"
              [iconClass]="'fa fa-times'"
              (clicked)="cancel()"
            />
          </div>

          <!-- Step Indicator -->
          <div class="wizard-steps">
            @for (step of steps; track step.id; let i = $index) {
              @if (!step.skipped || step.id === 'payment') {
                <div class="wizard-step" [class]="getStepClass(step, i)" (click)="goToStep(i)">
                  <div class="step-indicator">
                    @if (step.completed && !step.skipped) {
                      <i class="fa fa-check"></i>
                    } @else if (step.skipped) {
                      <i class="fa fa-forward"></i>
                    } @else {
                      <span>{{ i + 1 }}</span>
                    }
                  </div>
                  <div class="step-content">
                    <div class="step-title">
                      <i class="fa {{ step.icon }} me-1"></i>
                      {{ step.title }}
                    </div>
                    @if (!step.required && step.id !== 'review') {
                      <span class="step-optional">{{ 'eventWizard.optional' | translate }}</span>
                    }
                  </div>
                </div>
                @if (i < steps.length - 1) {
                  <div class="step-connector" [class.completed]="step.completed"></div>
                }
              }
            }
          </div>
        </div>
      </div>

      <!-- Step Content -->
      <div class="card mb-5">
        <div class="card-body">
          @switch (currentStep.id) {
            @case ('basic') {
              <app-wizard-step-basic
                [eventData]="eventData"
                [showValidation]="showValidation"
                [category]="selectedCategory"
                (dataChange)="onBasicInfoChange($event)"
              />
            }
            @case ('classes') {
              <app-wizard-step-classes
                [event]="event"
                (classesChanged)="onClassesChanged($event)"
              />
            }
            @case ('capacity') {
              <app-wizard-step-capacity
                [event]="event"
                (capacityChanged)="onCapacityChanged($event)"
              />
            }
            @case ('packages') {
              <app-wizard-step-packages
                [event]="event"
                (packagesChanged)="onPackagesChanged($event)"
              />
            }
            @case ('schedule') {
              <app-wizard-step-schedule
                [event]="event"
                (schedulesChanged)="onSchedulesChanged($event)"
              />
            }
            @case ('services') {
              <app-wizard-step-services
                [event]="event"
                (eventUpdated)="onEventUpdated($event)"
              />
            }
            @case ('contacts') {
              <app-wizard-step-contacts
                [event]="event"
                (eventUpdated)="onEventUpdated($event)"
              />
            }
            @case ('sponsors') {
              <app-wizard-step-sponsors
                [event]="event"
                (eventUpdated)="onEventUpdated($event)"
              />
            }
            @case ('payment') {
              <app-wizard-step-payment
                [event]="event"
                [company]="company"
                (eventUpdated)="onEventUpdated($event)"
              />
            }
            @case ('review') {
              <app-wizard-step-review
                [event]="event"
                [company]="company"
                [steps]="steps"
                [category]="selectedCategory"
                (goToStep)="goToStep($event)"
              />
            }
          }
        </div>

        <!-- Navigation Footer -->
        <div class="card-footer">
          <div class="d-flex flex-wrap align-items-center gap-2">
            @if (!isFirstStep) {
              <app-button
                [variant]="'outline'"
                [label]="'common.back' | translate"
                [iconClass]="'fa fa-arrow-left'"
                (clicked)="previousStep()"
              />
            }
            <div class="d-flex flex-wrap gap-2 ms-auto">
              @if (canSkip && !isLastStep) {
                <app-button
                  [variant]="'outline'"
                  [label]="'eventWizard.skip' | translate"
                  [iconClass]="'fa fa-forward'"
                  (clicked)="skipStep()"
                />
              }
              @if (!isLastStep) {
                <app-button
                  [variant]="'primary'"
                  [label]="saving ? ('eventWizard.saving' | translate) : ('eventWizard.next' | translate)"
                  [iconClass]="saving ? '' : 'fa fa-arrow-right'"
                  [disabled]="saving"
                  [loading]="saving"
                  (clicked)="nextStep()"
                />
              } @else {
                <app-button
                  [variant]="'outline'"
                  [label]="'eventWizard.saveAsDraft' | translate"
                  [iconClass]="'fa fa-save'"
                  [disabled]="saving"
                  (clicked)="saveAsDraft()"
                />
                <app-button
                  [variant]="'primary'"
                  [label]="saving ? ('eventWizard.publishing' | translate) : ('eventWizard.publishEvent' | translate)"
                  [iconClass]="saving ? '' : 'fa fa-rocket'"
                  [disabled]="saving || !canPublish"
                  [loading]="saving"
                  (clicked)="publishEvent()"
                />
              }
            </div>
          </div>
        </div>
      </div>
    }
  </div>
</div>
